// @docs:
//   - docs/core/architecture/SHIPS_PAPERS.md  § Invitations
// ==============================================
// /dashboard/join/:token — accept an invitation aboard a vessel (Ship's Papers).
// Reached from the invitation email. AuthGuard has already made sure the
// person is signed in (or has registered); we show what they were invited to
// and one button. Only the account the invitation was sent to may accept it:
// signed in with another one, the page says whose it is (s***@example.com) and
// offers to sign out and come back here.
// ==============================================
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { LifebuoyIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import { useToastStore } from '../../store/toastStore';
import { ROLE_LABEL } from '../../types/papers';

const body = (e: unknown) => (isAxiosError(e) ? (e.response?.data as { error?: string; code?: string } | undefined) : undefined);
const msg = (e: unknown, fallback: string) => body(e)?.error || fallback;

export function JoinVesselPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, logout } = useAuth();
  const addToast = useToastStore((s) => s.addToast);
  const { data, isLoading, error } = useQuery({ queryKey: ['papers-invite', token], queryFn: () => api.papersInviteInfo(token), retry: false });
  const accept = useMutation({
    mutationFn: () => api.papersAcceptInvite(token),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['papers-access'] });
      addToast({ type: 'success', title: `Welcome aboard ${data?.vesselName ?? ''}`.trim() });
      navigate(`/dashboard/papers?vessel=${r.vesselId}`, { replace: true });
    },
  });
  const switchAccount = useMutation({
    mutationFn: () => logout(),
    onSettled: () => navigate('/login', { replace: true, state: { from: { pathname: `/dashboard/join/${token}` } } }),
  });

  const refusedAsWrongAccount = accept.isError && body(accept.error)?.code === 'INVITE_WRONG_ACCOUNT';
  const wrongAccount = !!data && (!data.matchesAccount || refusedAsWrongAccount);

  return (
    <div className="p-6 max-w-xl mx-auto mt-10">
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
        <LifebuoyIcon className="w-10 h-10 mx-auto text-amber-400 mb-3" />
        {isLoading ? <p className="text-slate-300">Checking your invitation…</p>
          : error || !data ? (<>
            <h1 className="text-xl font-bold text-white mb-2">This invitation isn't valid any more</h1>
            <p className="text-base text-slate-300">Invitations last 14 days, and end if the person who sent it is no longer aboard. Ask the captain to send a new one.</p>
          </>) : wrongAccount ? (<>
            <h1 className="text-xl font-bold text-white mb-2">This invitation is for another account</h1>
            {refusedAsWrongAccount && data.matchesAccount
              ? <p className="text-base text-slate-300 mb-2">{msg(accept.error, '')}</p>
              : <p className="text-base text-slate-300 mb-2">This invitation was sent to <b className="text-white">{data.invitedEmailHint}</b>.</p>}
            <p className="text-base text-slate-300 mb-6">You're signed in as {user?.email ?? 'someone else'}. Sign in with the address the invitation went to, and you'll come straight back here.</p>
            <button type="button" onClick={() => switchAccount.mutate()} disabled={switchAccount.isPending} className="px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105 disabled:opacity-60">
              {switchAccount.isPending ? 'Signing out…' : 'Sign out and continue'}
            </button>
          </>) : (<>
            <h1 className="text-xl font-bold text-white mb-2">You're invited aboard {data.vesselName}</h1>
            <p className="text-base text-slate-300 mb-1">{data.inviterName ?? 'The captain'} invited you as <b className="text-white">{ROLE_LABEL[data.role]}</b>.</p>
            <p className="text-sm text-slate-400 mb-6">You'll see the vessel's papers you're entitled to, and your own certificates travel with you between vessels.</p>
            {accept.isError && <p role="alert" className="text-sm text-red-300 mb-3">{msg(accept.error, 'Could not accept the invitation.')}</p>}
            <button type="button" onClick={() => accept.mutate()} disabled={accept.isPending} className="px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105 disabled:opacity-60">
              {accept.isPending ? 'Joining…' : 'Accept and come aboard'}
            </button>
          </>)}
      </div>
    </div>
  );
}
